import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

// 2026-10-22 v2: where the minutes go — Avito / waiting for our check / our processing; Avito failures and queue.
const PERIODS = [1, 7, 30];
const WHO = [
  { k: "clients", t: "Клиенты" },
  { k: "admins", t: "Мои поиски" },
];
const COUNTERS: { k: string; t: string; hint: string }[] = [
  { k: "stub", t: "Заглушка вместо выдачи", hint: "Авито отдал пустую заглушку 3 раза подряд — проверка повторена через 30 с" },
  { k: "empty", t: "Пустая выдача", hint: "0 объявлений там, где обычно есть — загружено ещё раз" },
  { k: "item_fail", t: "Не открылась страница объявления", hint: "блокировка или ошибка — повтор с другого IP" },
  { k: "unreadable", t: "Убрано: страница не открылась", hint: "Авито блокировал все 3 попытки — не откладываем, чтобы не прислать поздно" },
  { k: "serp_card", t: "Отправлено без продавца", hint: "страница объявления не открылась — ушло сразу с фото и ценой из выдачи" },
  { k: "deferred", t: "Отложено на следующую проверку", hint: "не хватило времени/страниц или страница не открылась" },
  { k: "stale", t: "Не отправлено: опоздало", hint: "ждало у нас дольше 2,5 минуты — не шлём старьё" },
  { k: "page2", t: "Догружена 2-я страница", hint: "широкий поиск: вся 1-я страница новее прошлой проверки — смотрим и 2-ю, чтобы не потерять" },
  { k: "too_old", t: "Не отправлено: старое поднятое", hint: "опубликовано больше 6 часов назад и поднято наверх — это не новое" },
];

// 2026-10-27: the fast-mode loader's load against its capacity
const LOAD_STATUS: Record<string, { t: string; c: string }> = {
  ok: { t: "в норме", c: "#16a34a" },
  warn: { t: "загружен заметно", c: "#d97706" },
  full: { t: "не хватает мощности", c: "#dc2626" },
  silent: { t: "загрузчик молчит", c: "#dc2626" },
  unknown: { t: "нет данных", c: "#666" },
};

function pct(v?: number | null) {
  return v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`;
}

function num(v?: number | null) {
  return v === null || v === undefined ? "—" : String(v).replace(".", ",");
}

function LoadCard({ load }: { load: any }) {
  if (!load || !load.on) return null;
  const c = load.current;
  const st = LOAD_STATUS[load.status] || LOAD_STATUS.unknown;
  return (
    <div style={styles.big}>
      <div style={{ ...styles.bigValue, color: st.c }}>{c ? pct(c.util) : "—"}</div>
      <div style={styles.bigTitle}>загрузчик занят · {st.t}</div>
      {c && (
        <div style={styles.hint}>
          {num(c.per_min)} загрузок/мин из ~{Math.round(c.cap)} · неудачных {c.fail_pct}% · очередь {c.waiting} ·
          свободных вкладок {c.free_tabs} · загрузка {num(c.median_s)} с · данные на {c.at}
        </div>
      )}
      {load.peak && (
        <div style={styles.hint}>
          пик за сутки: {pct(load.peak.util)} ({num(load.peak.per_min)} загрузок/мин) · очередь до {load.peak.waiting}
        </div>
      )}
      {load.hours && load.hours.length > 0 && (
        <div style={styles.loadBars}>
          {load.hours.map((h: any) => (
            <div key={h.hour} style={styles.loadCol} title={`${h.hour}:00 — ${pct(h.util)}`}>
              <div
                style={{
                  ...styles.loadBar,
                  height: `${Math.max(4, Math.min(100, h.util * 100))}%`,
                  backgroundColor: h.util >= 0.85 ? "#dc2626" : h.util >= 0.6 ? "#d97706" : "#16a34a",
                }}
              />
              <div style={styles.loadHour}>{h.hour}</div>
            </div>
          ))}
        </div>
      )}
      {load.expires && (
        <div style={styles.hint}>
          бэкконнект до {load.expires} (осталось {num(load.days_left)} дн.)
        </div>
      )}
      <div style={styles.note}>
        Столбики — самая высокая загрузка за каждый час. Если стабильно выше 80% или появляется очередь — пора
        второй доступ бэкконнекта (+5 подключений).
      </div>
    </div>
  );
}

function m(v?: number | null) {
  return v === null || v === undefined ? "—" : `${String(v).replace(".", ",")} мин`;
}

function dayLabel(iso: string) {
  const [, mo, d] = iso.split("-");
  return `${d}.${mo}`;
}

export default function AdminSpeed() {
  const navigate = useNavigate();
  const [days, setDays] = useState(1);
  const [who, setWho] = useState("clients");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [load, setLoad] = useState<any>(null);

  useEffect(() => {
    api.get("/admin/load").then((r: any) => setLoad(r.data)).catch(() => setLoad(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/speed?days=${days}&who=${who}`).then((r: any) => setData(r.data)).finally(() => setLoading(false));
  }, [days, who]);

  const max = Math.max(1, ...((data?.by_day || []).map((d: any) => d.total || 0)));
  const counters = data?.counters || {};

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/admin")} style={styles.back}>← Админ-панель</button>
      <h1 style={styles.title}>Скорость доставки</h1>
      <LoadCard load={load} />
      <div style={styles.chips}>
        {PERIODS.map((p) => (
          <button key={p} onClick={() => setDays(p)} style={{ ...styles.chip, ...(p === days ? styles.chipOn : {}) }}>
            {p === 1 ? "Сегодня" : `${p} дней`}
          </button>
        ))}
      </div>
      <div style={styles.chips}>
        {WHO.map((w) => (
          <button key={w.k} onClick={() => setWho(w.k)} style={{ ...styles.chip, ...(w.k === who ? styles.chipOn : {}) }}>
            {w.t}
          </button>
        ))}
      </div>

      {!data ? (
        <div style={styles.hint}>{loading ? "Загрузка…" : "Нет данных"}</div>
      ) : (
        <>
          <div style={styles.big}>
            <div style={styles.bigValue}>{m(data.total.median)}</div>
            <div style={styles.bigTitle}>от публикации на Авито до уведомления (медиана)</div>
            <div style={styles.hint}>9 из 10 — быстрее {m(data.total.p90)} · свежих объявлений: {data.n}</div>
          </div>

          <div style={styles.grid3}>
            <div style={styles.card}>
              <div style={styles.cardValue}>{m(data.avito.median)}</div>
              <div style={styles.cardTitle}>Авито показал</div>
              <div style={styles.cardHint}>от публикации до появления в поиске Авито — не от нас</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.cardValue, color: "#d97706" }}>{m(data.wait.median)}</div>
              <div style={styles.cardTitle}>ждали проверку</div>
              <div style={styles.cardHint}>от появления до нашей проверки — зависит от интервала тарифа</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.cardValue, color: "#16a34a" }}>{m(data.proc.median)}</div>
              <div style={styles.cardTitle}>обработка</div>
              <div style={styles.cardHint}>от «увидели» до уведомления; 9 из 10 — быстрее {m(data.proc.p90)}</div>
            </div>
          </div>
          {data.late.n > 0 && (
            <div style={styles.note}>
              Ещё {data.late.n} уведомл. — поздние попадания (снижение цены, правка, перевыставление): объявление
              попало в поиск позже чем через 30 мин после публикации. В цифры выше не входят.
            </div>
          )}

          <h2 style={styles.subtitle}>Сбои Авито и очередь</h2>
          <div style={styles.list}>
            {COUNTERS.map((c) => (
              <div key={c.k} style={styles.row}>
                <div style={{ minWidth: 0 }}>
                  <div style={styles.rowTitle}>{c.t}</div>
                  <div style={styles.rowHint}>{c.hint}</div>
                </div>
                <div style={{ ...styles.rowTitle, flexShrink: 0 }}>{counters[c.k] ?? 0}</div>
              </div>
            ))}
            <div style={styles.note}>Счётчики — за выбранный период и по выбранным поискам (клиенты / мои), раздельно с 23.10.</div>
          </div>

          <h2 style={styles.subtitle}>По тарифам</h2>
          <div style={styles.list}>
            {data.by_plan.map((p: any) => (
              <div key={p.plan} style={styles.row}>
                <div>
                  <div style={styles.rowTitle}>{p.plan}</div>
                  <div style={styles.rowHint}>уведомлений: {p.n}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={styles.rowTitle}>{m(p.total)}</div>
                  <div style={styles.rowHint}>проверка+обработка: {m(p.ours)} · обработка: {m(p.proc)}</div>
                </div>
              </div>
            ))}
            {data.by_plan.length === 0 && <div style={styles.hint}>Нет уведомлений за период</div>}
          </div>

          {days > 1 && (
            <>
              <h2 style={styles.subtitle}>По дням</h2>
              <div style={styles.list}>
                {data.by_day.map((d: any) => (
                  <div key={d.day} style={styles.dayRow}>
                    <div style={styles.dayLabel}>{dayLabel(d.day)}</div>
                    <div style={styles.barBox}>
                      {d.total !== null && <div style={{ ...styles.bar, width: `${Math.max(4, (d.total / max) * 100)}%` }} />}
                    </div>
                    <div style={styles.dayValue}>{d.total === null ? "—" : m(d.total)}</div>
                    <div style={styles.dayOurs}>{d.ours === null ? "" : `наши ${m(d.ours)}`}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {data.slow.length > 0 && (
            <>
              <h2 style={styles.subtitle}>Самые медленные поиски</h2>
              <div style={styles.list}>
                {data.slow.map((s: any) => (
                  <div key={s.id} style={styles.row}>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.rowTitle}>#{s.id} {s.name}</div>
                      <div style={styles.rowHint}>@{s.username || "—"} · уведомлений: {s.n}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={styles.rowTitle}>{m(s.total)}</div>
                      <div style={styles.rowHint}>Авито {m(s.avito)} · обработка {m(s.proc)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={styles.explain}>
            Объявление появляется в поиске Авито где-то между двумя нашими проверками — берём середину. Время публикации —
            точное время Авито из выдачи поиска (раньше — со страницы объявления). Большую часть «Авито показал»
            занимает модерация: пока она идёт, объявления нет в поиске ни у кого. «Обработка» — точное время от момента, когда наша проверка
            увидела объявление, до уведомления. Если растёт «обработка» — проблема у нас (прокси, блокировки страниц,
            очередь); если «Авито показал» — Авито долго держит объявления.
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  subtitle: { fontSize: "16px", fontWeight: 600, margin: "20px 0 10px 0", color: "var(--tg-theme-text-color, #000)" },
  chips: { display: "flex", gap: "6px", marginBottom: "10px" },
  chip: { flex: 1, padding: "8px 0", borderRadius: "10px", border: "none", fontSize: "13px", cursor: "pointer", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)" },
  chipOn: { backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" },
  big: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "16px", padding: "16px", textAlign: "center", margin: "4px 0 12px 0", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  bigValue: { fontSize: "32px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  bigTitle: { fontSize: "13px", color: "var(--tg-theme-text-color, #000)", marginTop: "4px" },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" },
  card: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "14px", padding: "10px 6px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", minWidth: 0 },
  cardValue: { fontSize: "18px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)" },
  cardTitle: { fontSize: "12px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)", marginTop: "4px" },
  cardHint: { fontSize: "10px", color: "var(--tg-theme-hint-color, #666)", marginTop: "4px", lineHeight: 1.3 },
  hint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginTop: "6px" },
  note: { fontSize: "11px", color: "var(--tg-theme-hint-color, #666)", marginTop: "10px", lineHeight: 1.4 },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  row: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "12px", padding: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" },
  rowTitle: { fontSize: "14px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)", wordBreak: "break-word" },
  rowHint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginTop: "2px" },
  dayRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" },
  dayLabel: { width: "38px", color: "var(--tg-theme-hint-color, #666)" },
  barBox: { flex: 1, height: "10px", borderRadius: "5px", backgroundColor: "var(--tg-theme-secondary-bg-color, #eee)", overflow: "hidden" },
  bar: { height: "100%", borderRadius: "5px", backgroundColor: "var(--tg-theme-button-color, #2481cc)" },
  dayValue: { width: "56px", textAlign: "right", color: "var(--tg-theme-text-color, #000)" },
  dayOurs: { width: "78px", textAlign: "right", color: "#16a34a", fontSize: "11px" },
  loadBars: { display: "flex", alignItems: "flex-end", gap: "2px", height: "64px", marginTop: "10px" },
  loadCol: { flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", minWidth: 0 },
  loadBar: { width: "100%", borderRadius: "3px" },
  loadHour: { fontSize: "8px", color: "var(--tg-theme-hint-color, #666)", marginTop: "2px" },
  explain: { fontSize: "11px", color: "var(--tg-theme-hint-color, #666)", marginTop: "18px", lineHeight: 1.4 },
};
